import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  UnlockOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { adminService, ClientDetailResponse, AuditLog } from '../../services/admin.service';
import { safeDate } from '../../utils/formatters';
import ConfirmActionModal from '../../components/admin/ConfirmActionModal';
import AuditLogTable, { AuditLogEntry } from '../../components/admin/AuditLogTable';

const AdminClientDetailPage: React.FC = () => {
  const { id: tenantId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State for client data
  const [clientDetail, setClientDetail] = useState<ClientDetailResponse | null>(null);
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [clientError, setClientError] = useState<string | null>(null);

  // State for audit logs
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);

  // State for modals
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // State for UI
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [toasts, setToasts] = useState<Array<{ id: string; type: string; message: string }>>([]);

  // Load client details
  useEffect(() => {
    const loadClientDetail = async () => {
      if (!tenantId) {
        setClientError('Client ID is required');
        setIsLoadingClient(false);
        return;
      }

      try {
        setIsLoadingClient(true);
        setClientError(null);
        const response = await adminService.getClientDetail(tenantId);

        if (!response.tenant) {
          setClientError('Client not found');
        } else {
          setClientDetail(response);
        }
      } catch (error: any) {
        setClientError(error?.message || 'Failed to load client details');
      } finally {
        setIsLoadingClient(false);
      }
    };

    loadClientDetail();
  }, [tenantId]);

  // Load audit logs
  useEffect(() => {
    const loadAuditLogs = async () => {
      if (!tenantId) return;

      try {
        setIsLoadingAudit(true);
        setAuditError(null);
        const response = await adminService.getAuditLog({
          tenantId,
          limit: 10,
        });

        if (response.logs && Array.isArray(response.logs)) {
          const mappedLogs: AuditLogEntry[] = response.logs.map((log: AuditLog) => ({
            id: log.auditId,
            timestamp: log.timestamp,
            action: log.action,
            targetType: 'Client',
            targetId: log.tenantId || tenantId,
            targetName: clientDetail?.tenant.tenantName || tenantId,
            actor: log.actor || 'System',
            result: log.status === 'SUCCESS' ? 'success' : 'error',
            errorMessage: log.status === 'SUCCESS' ? undefined : `Action failed`,
            details: log.details,
            metadata: {
              auditId: log.auditId,
              resourceId: log.resourceId,
            },
          }));
          setAuditLogs(mappedLogs);
        }
      } catch (error: any) {
        setAuditError(error?.message || 'Failed to load audit logs');
      } finally {
        setIsLoadingAudit(false);
      }
    };

    loadAuditLogs();
  }, [tenantId, clientDetail?.tenant.tenantName]);

  // Helper: Show toast notification
  const showToast = (type: 'success' | 'error' | 'info' | 'warning', message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  };

  // Handler: Suspend client
  const handleSuspendClient = async () => {
    if (!tenantId) return;

    setIsProcessing(true);
    try {
      const response = await adminService.suspendClient(tenantId, suspendReason || undefined);
      if (response.success) {
        showToast('success', `Client suspended successfully`);
        setSuspendReason('');
        setShowSuspendModal(false);

        // Refresh client data
        const updated = await adminService.getClientDetail(tenantId);
        if (updated.tenant) {
          setClientDetail(updated);
        }
      } else {
        showToast('error', response.error || 'Failed to suspend client');
      }
    } catch (error: any) {
      showToast('error', error?.message || 'Error suspending client');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler: Reactivate client
  const handleReactivateClient = async () => {
    if (!tenantId) return;

    setIsProcessing(true);
    try {
      const response = await adminService.reactivateClient(tenantId);
      if (response.success) {
        showToast('success', `Client reactivated successfully`);
        setShowReactivateModal(false);

        // Refresh client data
        const updated = await adminService.getClientDetail(tenantId);
        if (updated.tenant) {
          setClientDetail(updated);
        }
      } else {
        showToast('error', response.error || 'Failed to reactivate client');
      }
    } catch (error: any) {
      showToast('error', error?.message || 'Error reactivating client');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler: Navigate to audit page filtered by tenant
  const handleViewAudit = () => {
    navigate(`/admin/audit?tenantId=${tenantId}`);
  };

  // Loading state
  if (isLoadingClient) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading client details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (clientError || !clientDetail?.tenant) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 dark:text-red-400 text-2xl mb-4">
            <ExclamationCircleOutlined />
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {clientError || 'Client not found'}
          </p>
          <button
            onClick={() => navigate('/admin/clients')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <ArrowLeftOutlined className="mr-2" />
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const client = clientDetail.tenant;
  const isSuspended = client.status === 'SUSPENDED';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 sm:py-8 lg:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Toast Notifications */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`p-4 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in ${
                toast.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                  : toast.type === 'error'
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                  : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
              }`}
            >
              {toast.type === 'success' && <CheckCircleOutlined />}
              {toast.type === 'error' && <ExclamationCircleOutlined />}
              <span>{toast.message}</span>
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/clients')}
            className="mb-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2 transition-colors"
          >
            <ArrowLeftOutlined className="text-lg" />
            <span>Back to Clients</span>
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {client.tenantName}
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">{client.email}</p>
            </div>

            <div className="flex items-center gap-2">
              {/* Status Badge */}
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  client.status === 'ACTIVE'
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                    : client.status === 'SUSPENDED'
                    ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}
              >
                {isSuspended ? <LockOutlined /> : <UnlockOutlined />}
                {client.status}
              </div>
            </div>
          </div>
        </div>

        {/* Suspension Warning Banner */}
        {isSuspended && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <ExclamationCircleOutlined className="text-red-600 dark:text-red-400 text-lg flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                  Client Account Suspended
                </p>
                <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                  This client's account is currently suspended. All services and operations are restricted.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Key Metrics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Email Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
              Email
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
              {client.email}
            </p>
          </div>

          {/* Plan Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
              Plan
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {client.status === 'SUSPENDED' ? 'N/A (Suspended)' : 'Professional'}
            </p>
          </div>

          {/* Created At Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
              Created
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {safeDate(client.createdAt)}
            </p>
          </div>

          {/* Balance / Deuda Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
              Balance
            </p>
            <div className="flex items-baseline gap-2">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                ${clientDetail.totalCost?.toFixed(2) || '0.00'}
              </p>
              {clientDetail.deuda && clientDetail.deuda > 0 && (
                <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                  Debt: ${clientDetail.deuda.toFixed(2)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8" aria-label="Tabs">
            {['overview', 'services', 'audit'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Client Details Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Client Details
                  </h2>
                  <button
                    onClick={() => window.location.reload()}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Refresh"
                  >
                    <ReloadOutlined className="text-lg" />
                  </button>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tenant ID
                      </label>
                      <p className="text-sm font-mono text-gray-900 dark:text-white break-all">
                        {client.tenantId}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {client.tenantName}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {client.email}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <p className={`text-sm font-medium ${
                        client.status === 'ACTIVE'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {client.status}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Total Cost
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        ${clientDetail.totalCost?.toFixed(2) || '0.00'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Debt
                      </label>
                      <p className={`text-sm font-medium ${
                        clientDetail.deuda && clientDetail.deuda > 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        ${clientDetail.deuda?.toFixed(2) || '0.00'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Created At
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {safeDate(client.createdAt, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Updated
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {safeDate(client.updatedAt, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Admin Actions
                  </h2>
                </div>
                <div className="p-6 space-y-3">
                  {isSuspended ? (
                    <button
                      onClick={() => setShowReactivateModal(true)}
                      disabled={isProcessing}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <UnlockOutlined className="text-lg" />
                      <span>Reactivate Client</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowSuspendModal(true)}
                      disabled={isProcessing}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <LockOutlined className="text-lg" />
                      <span>Suspend Client</span>
                    </button>
                  )}

                  <button
                    onClick={handleViewAudit}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <FileTextOutlined className="text-lg" />
                    <span>View Full Audit Log</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Client Services ({clientDetail.services?.length || 0})
                </h2>
              </div>

              {clientDetail.services && clientDetail.services.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                          Service Name
                        </th>
                        <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientDetail.services.map((service) => (
                        <tr
                          key={service.serviceId}
                          className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                          onClick={() =>
                            navigate(`/admin/services/${service.serviceId}`)
                          }
                        >
                          <td className="px-6 py-4 text-gray-900 dark:text-gray-100 font-medium">
                            {service.name}
                          </td>
                          <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                            {service.type}
                          </td>
                          <td className="px-6 py-4">
                            <div
                              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                service.status === 'RUNNING'
                                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                                  : service.status === 'STOPPED'
                                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                  : service.status === 'ERROR'
                                  ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                                  : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
                              }`}
                            >
                              {service.status}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-xs">
                            {safeDate(service.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No services found for this client
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Audit Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Audit Log (Latest 10)
                </h2>
              </div>

              {isLoadingAudit ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading audit logs...</p>
                </div>
              ) : auditError ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center gap-3">
                    <ExclamationCircleOutlined className="text-red-600 dark:text-red-400 text-lg" />
                    <p className="text-sm text-red-600 dark:text-red-400">{auditError}</p>
                  </div>
                </div>
              ) : (
                <AuditLogTable logs={auditLogs} loading={isLoadingAudit} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmActionModal
        isOpen={showSuspendModal}
        onClose={() => {
          setShowSuspendModal(false);
          setSuspendReason('');
        }}
        onConfirm={handleSuspendClient}
        title="Suspend Client Account"
        description="This action will suspend all client services and operations. The client will not be able to access their account or manage services."
        confirmText="Suspend"
        cancelText="Cancel"
        variant="danger"
        showReasonField={true}
        reasonPlaceholder="Enter reason for suspension (optional)"
        impactMessage="All services will be restricted. The client will not be able to perform any operations."
        loading={isProcessing}
        onReasonChange={setSuspendReason}
      />

      <ConfirmActionModal
        isOpen={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        onConfirm={handleReactivateClient}
        title="Reactivate Client Account"
        description="This action will restore access to the client account and all their services."
        confirmText="Reactivate"
        cancelText="Cancel"
        variant="warning"
        impactMessage="The client will regain full access to their account and services."
        loading={isProcessing}
      />
    </div>
  );
};

export default AdminClientDetailPage;
